import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { TrendingDown, Phone } from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/avskrivning-husbil";

const FAQ = [
  {
    q: "Hur snabbt tappar en husbil i värde?",
    a: "En ny husbil tappar ca 15–20 % första året. Därefter ca 5–8 % per år de följande fem åren. Efter år 6–7 stabiliseras värdet och kvalitetsmärken som Rapido håller sig ovanligt bra.",
  },
  {
    q: "Vilka husbilar håller värdet bäst?",
    a: "Rapido, Hymer, Bürstner och Concorde är kända för hög värdehållning. Rapido plåtis (V62/V68) med litiumbatteri, dieselvärme och solpaneler tappar ovanligt lite.",
  },
  {
    q: "Vad påverkar värdeminskningen mest?",
    a: "1) Servicehistorik — komplett historik ger 5–10 % högre värde. 2) Fuktstatus — fuktskador halverar värdet direkt. 3) Utrustning — litium, dieselvärme, solpaneler höjer. 4) Miltal — under 5 000 mil/år är standard.",
  },
  {
    q: "Är det ekonomiskt att köpa ny eller begagnad husbil?",
    a: "Begagnad 3–5 år: bästa affären ekonomiskt. Den värsta värdeminskningen är över, men fordonet har lång livslängd kvar. Vi rekommenderar 2020–2023 års modeller för kunder som prioriterar ekonomi.",
  },
];

export const Route = createFileRoute("/verktyg/avskrivning-husbil")({
  head: () => ({
    meta: [
      { title: "Avskrivning husbil — räkna värdeminskning över tid | AK Husbil" },
      { name: "description", content: "Räkna ut hur mycket husbilen tappar i värde per år. Realistisk kurva för Rapido och andra kvalitetsmärken. Jämför nybil vs begagnat." },
      { name: "keywords", content: "avskrivning husbil, husbil värdeminskning, husbil andrahandsvärde, husbil värde per år, husbil ekonomi" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Avskrivning husbil — räkna värdeminskning" },
      { property: "og:description", content: "Se hur mycket din husbil tappar i värde år för år." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Räkna avskrivning på husbil",
          description: "Så räknar du ut vad husbilen är värd om 3, 5 eller 10 år.",
          totalTime: "PT3M",
          step: [
            { "@type": "HowToStep", name: "Startpris", text: "Ta nybilspriset (eller inköpspriset om begagnad)." },
            { "@type": "HowToStep", name: "År 1", text: "Räkna bort 15–20 % för första året. Nya husbilar tappar mest i början." },
            { "@type": "HowToStep", name: "År 2–6", text: "Räkna bort 5–8 % per år av det aktuella värdet." },
            { "@type": "HowToStep", name: "År 7+", text: "Värdet stabiliseras. Kvalitetsmärken som Rapido håller sig extremt väl." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: Page,
});

function fmt(n: number): string {
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(n);
}

function Page() {
  const [price, setPrice] = useState(1200000);
  const [firstYear, setFirstYear] = useState(17);
  const [yearly, setYearly] = useState(7);
  const [years, setYears] = useState(5);

  const schedule = useMemo(() => {
    const rows: { year: number; value: number; loss: number }[] = [];
    let v = price;
    for (let y = 1; y <= years; y++) {
      const rate = y === 1 ? firstYear / 100 : yearly / 100;
      const loss = v * rate;
      v -= loss;
      rows.push({ year: y, value: Math.round(v), loss: Math.round(loss) });
    }
    return rows;
  }, [price, firstYear, yearly, years]);

  const totalLoss = price - (schedule[schedule.length - 1]?.value ?? price);
  const lossPerYear = totalLoss / years;

  return (
    <>
      <PageHeader
        label="Verktyg · Räkna hemma"
        title={<>Avskrivning <span className="font-italic-display text-accent">husbil</span>.</>}
        sub="Räkna ut vad din husbil är värd om 3, 5 eller 10 år. Baserat på verkliga försäljningsdata från begagnatmarknaden."
      />

      <main className="max-w-3xl mx-auto px-4 py-10 space-y-10">
        <section className="rounded-2xl border-2 border-primary/20 bg-card p-6 md:p-8">
          <div className="flex items-center gap-2 mb-6">
            <TrendingDown className="text-primary" />
            <h2 className="text-xl font-bold">Räkna värdeminskning</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium">Nybilspris (kr)</span>
              <input type="number" step="10000" min="100000" value={price} onChange={(e) => setPrice(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Antal år</span>
              <input type="number" step="1" min="1" max="15" value={years} onChange={(e) => setYears(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">År 1-avskrivning (%)</span>
              <input type="number" step="1" min="5" max="30" value={firstYear} onChange={(e) => setFirstYear(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Årlig avskrivning år 2+ (%)</span>
              <input type="number" step="1" min="2" max="15" value={yearly} onChange={(e) => setYearly(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Värde år {years}</div>
              <div className="text-2xl font-bold">{fmt(schedule[schedule.length - 1]?.value ?? price)} kr</div>
            </div>
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Total värdeminskning</div>
              <div className="text-2xl font-bold">{fmt(totalLoss)} kr</div>
            </div>
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Snitt per år</div>
              <div className="text-2xl font-bold">{fmt(lossPerYear)} kr</div>
            </div>
          </div>

          <table className="mt-8 w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr><th className="py-2">År</th><th>Värdeminskning</th><th>Värde</th></tr>
            </thead>
            <tbody>
              {schedule.map((r) => (
                <tr key={r.year} className="border-b">
                  <td className="py-2">{r.year}</td>
                  <td>-{fmt(r.loss)} kr</td>
                  <td className="font-semibold">{fmt(r.value)} kr</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-6">Vanliga frågor</h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <div key={i} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Vad är just din husbil värd idag?</h2>
          <p className="mb-6 opacity-90">Vi värderar utan kostnad — ring så tar vi ett samtal.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
